<template>
  <div>
    <v-expansion-panels flat accordion multiple class="expansion-panel-wrapper">
      <v-expansion-panel
        v-for="group in groupedFindings"
        :key="group.eventTrigger"
        :value="group.eventTrigger"
      >
        <v-expansion-panel-title
          class="expansion-panel-header pl-4 pt-2 pb-2"
          @click="handleTriggerPanelClick"
        >
          <template v-slot:actions />
          <template v-slot:default="{ expanded }">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-icon :class="{ rotated: !expanded }" icon="$chevronUp"></v-icon>
                <div class="ml-1 subtitle2">
                  {{ $t(`tickets.eventSubTriggers.${group.eventTrigger}`) }}
                </div>
              </div>
              <div class="body2 ml-1">
                {{ group.numberOfFindings }} {{ $t("ticketDetails.findings") }}
              </div>
            </div>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="source-findings-container">
            <table-wrapper>
              <v-data-table
                :headers="tableHeaders"
                :items="group.sources"
                :single-expand="false"
                disable-pagination
                hide-default-footer
                :item-value="getSourceItemValue"
                show-expand
                fixed-header
                hide-default-header
                expand-icon="icon-chevron-down-xs"
              >
                <template #item="{ item, index, isExpanded, toggleExpand, internalItem }">
                  <tr
                    :id="`${index}`"
                    :class="{ 'row-no-border': isExpanded(internalItem) }"
                    data-testid="source-row"
                  >
                    <td class="w-10" id="chevron-col">
                      <v-icon
                        :class="{ rotated: !isExpanded(internalItem) }"
                        @click="toggleExpand(internalItem)"
                        icon="$chevronUp"
                      >
                      </v-icon>
                    </td>
                    <td class="w-80 body2">
                      <div class="findings-grid">
                        <div class="findings-grid__label">
                          {{ $t("ticketDetails.detectedIn") }}:
                        </div>
                        <div class="findings-grid__value">
                          <template v-if="isAttachment(item.source)">
                            <span class="subtitle2 mr-1" data-testid="attachment-file-name">
                              {{ item.fileName }}
                            </span>
                            <span class="body2 text-indigo-medium">
                              ({{ convertSize(item.fileSize || 0, 1) }})
                            </span>
                          </template>
                          <template v-else>
                            <span class="subtitle2" data-testid="non-attachment-source">{{
                              $t(`ticketDetails.foundInTypes.${item.source}`)
                            }}</span>
                          </template>
                        </div>
                      </div>
                    </td>
                    <td class="body2 text-right">
                      {{ item.findings.length }}
                    </td>
                  </tr>
                </template>
                <template #expanded-row="{ columns, item }">
                  <td class="w-10 expanded-row-border"></td>
                  <td
                    :colspan="columns.length"
                    class="word-break-all white-space-normal expanded-row-border"
                  >
                    <div class="expandable-content pl-4">
                      <div class="mr-4 instance-details findings-grid">
                        <template
                          v-for="(finding, findingIndex) in getSortedFindingTemplates(
                            item.findings
                          )"
                          :key="findingIndex"
                        >
                          <div class="findings-grid__row" v-html="finding"></div>
                          <div
                            v-if="findingIndex !== item.findings.length - 1"
                            class="findings-grid__divider"
                          />
                        </template>
                      </div>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </table-wrapper>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Finding, TicketDetails } from "@/_store/tickets/tickets.module";
import { FindingDetectedInType } from "@/_store/tickets/tickets.module";
import { convertSize } from "@coronet/cui";
import { trackGtmEvent } from "@/_helpers/analytics";
import { createFindingTemplate } from "@/_helpers/ticket-helpers";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import type {
  DlpSourceFinding,
  GroupedDlpFindings,
} from "@/components/tickets/ticket-preview-details/full-details/UnifiedEmailDlpFindingsFullDetails.vue";
import TableWrapper from "@/components/TableWrapper.vue";

export default defineComponent({
  components: {
    TableWrapper,
  },
  props: {
    groupedFindings: {
      type: Array as PropType<GroupedDlpFindings[]>,
      required: true,
    },
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const tableHeaders = [
      {
        title: "",
        key: "data-table-expand",
        sortable: false,
        headerProps: {
          "aria-label": i18n.t("ariaLabels.expanderColumn"),
        },
      },
      { key: "source", sortable: false },
      { key: "findingsCount", sortable: false },
    ];

    const handleTriggerPanelClick = () => {
      trackGtmEvent(GtmAction.TICKETS, `${GTMParams.EMAIL_FINDINGS_CLICKED}`);
    };

    const isAttachment = (source: FindingDetectedInType) => {
      return source === FindingDetectedInType.ATTACHMENT;
    };

    const getSourceItemValue = (item: DlpSourceFinding) => {
      return item.fileName || item.source;
    };

    const getSortedFindingTemplates = (findings: Finding[]) =>
      findings
        .slice()
        .sort((a, b) => {
          // if source exists - sort by source
          if (a.source) {
            return a.source.localeCompare(b.source!);
          }
          // if it's archive - sort by filePath
          if (a.filePath?.includes("#")) {
            return a.filePath.localeCompare(b.filePath);
          }
          if (a.typeUiView) {
            return a.typeUiView.localeCompare(b.typeUiView!);
          }
          return a.type.localeCompare(b.type);
        })
        .map((finding) => createFindingTemplate(finding, props.ticket!));

    return {
      tableHeaders,
      handleTriggerPanelClick,
      isAttachment,
      getSourceItemValue,
      convertSize,
      getSortedFindingTemplates,
    };
  },
});
</script>

<style scoped lang="scss">
.expansion-panel-header {
  height: 56px;
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 0 !important;
}

.v-expansion-panel:not(.v-expansion-panel--active):not(:last-child) {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 0 !important;
}

.source-findings-container {
  border-top: 1px solid rgb(var(--v-theme-indigo-pale));
}

.expanded-row-border {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale)) !important;
}

:deep(*) {
  .v-expansion-panel-text__wrapper {
    padding: 0 !important;
  }

  #chevron-col {
    padding-left: 40px !important;
  }

  table {
    tr {
      height: 64px;
    }
  }

  .v-data-table__expanded__content {
    box-shadow: unset !important;
  }

  .v-table__wrapper {
    box-shadow: unset !important;
    border-radius: unset !important;
  }

  .row-no-border {
    td {
      border: none !important;
    }
  }
}
</style>
